import type { Table } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Icon, Skeleton, Tooltip } from '~/components';
import { DataUnavailable } from '~/components/DataUnavailable';
import { cn } from '~/lib/cn';
import {
  TableBody,
  TableCell,
  TableRow,
} from '~/modules/artist-roster/components/Table';
import { SkeletonTable } from '~/modules/artist-roster/components/Table/components/SkeletonTable';
import type { ArtistDetails, AssetFamily } from '~/modules/artist-roster/types';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';

import { TrackPerformanceTooltip } from './TrackPerformanceTooltip';
import type { TrackTableMeta } from './TrackTable';

interface TrackTableBodyProps {
  table: Table<AssetFamily>;
  meta: TrackTableMeta;
  artistData: ArtistDetails;
  isFetchingTracks: boolean;
  isLoadingPerformance: boolean;
}

const PERFORMANCE_COLUMNS = [
  'streamsRTD',
  'streams',
  'organicStreams',
  'socialCreations',
];

export function TrackTableBody({
  table,
  meta,
  artistData,
  isFetchingTracks,
  isLoadingPerformance,
}: TrackTableBodyProps) {
  const { t } = useTranslation('roster');
  const { trackEvent } = useAnalytics('Artist Roster');

  const [
    isTrackDetailsEnabled,
    isAssetPerformanceEnabled,
    isCompareBenchmarkEnabled,
    isSetBenchmarksEnabled,
  ] = useFeatureFlags([
    FEATURE_FLAGS.WmgTrackDetails,
    FEATURE_FLAGS.WmgAssetPerformance,
    FEATURE_FLAGS.WmgTrackCompareBenchmark,
    FEATURE_FLAGS.WmgTrackSetBenchmarks,
  ]);

  if (isFetchingTracks) {
    return (
      <TableBody>
        <SkeletonTable
          size={12}
          headers={table.getHeaderGroups()[0].headers}
          firstLineClassName="w-3xl"
          secondLineClassName="w-2xl"
          cellClassName="pl-sm"
        />
      </TableBody>
    );
  }

  if (!table.getRowModel().rows?.length) {
    return (
      <TableBody>
        <TableRow>
          <TableCell colSpan={isAssetPerformanceEnabled ? 6 : 4}>
            <DataUnavailable
              title={t('trackList.noData.title')}
              subtitle={t('trackList.noData.caption', {
                artistName: meta?.artistName || 'artist',
              })}
              icon={
                <Icon
                  type="turntable"
                  size="4x"
                  variant="solid"
                  aria-hidden
                  className="text-default"
                />
              }
            />
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  const handleTrackTableRowClick = ({
    trackData,
  }: {
    trackData: AssetFamily;
  }) => {
    if (isTrackDetailsEnabled && meta) {
      if (artistData) {
        trackEvent('artistTrackListTrackClick', {
          artist_id: artistData.partyId,
          artist_name: artistData.name,
          track_name: trackData.title,
          // @TODO WMG1-246 Add Family ID when available.
          family_id: trackData.id,
        });
      }
    }
  };

  const adjustedBenchmark = isSetBenchmarksEnabled ? 1500000 : undefined;
  const adjustedBy = isSetBenchmarksEnabled ? 'Eden Afek' : undefined;
  const adjustmentReason = isSetBenchmarksEnabled
    ? 'adjustment reason'
    : undefined;

  return (
    <TableBody>
      {table.getRowModel().rows.map((row, index) => {
        const { streaming, streamingBenchmark, engagement } =
          row.original.performance ?? {};

        return (
          <TableRow
            key={row.id}
            aria-label={`Row for ${row.original?.id || 'track'}`}
            data-state={row.getIsSelected() && 'selected'}
            className={
              isTrackDetailsEnabled
                ? cn(
                    'group/row relative transition-all duration-300 ease-out hover:bg-emphasis-subtle',
                  )
                : ''
            }
          >
            {row.getVisibleCells().map((cell) => {
              let content;
              const isPerformanceColumn = PERFORMANCE_COLUMNS.includes(
                cell.column.id,
              );

              if (isPerformanceColumn && isLoadingPerformance) {
                content = (
                  <div className="space-y-sm pl-sm">
                    <Skeleton className="h-md w-3xl rounded-sm bg-level-04" />
                    <Skeleton className="h-md w-2xl rounded-sm bg-level-04" />
                  </div>
                );
              } else if (cell.column.id === 'title' && isTrackDetailsEnabled) {
                content = (
                  <Link
                    // @TODO WMG1-246 Replace track title with track family ID
                    to={`/roster/${meta.partyId}/${row.original.id}`}
                    className="group flex items-center gap-sm py-sm pl-lg"
                    onClick={() =>
                      handleTrackTableRowClick({ trackData: row.original })
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Link>
                );
              } else {
                content = (
                  <div
                    className={cn('flex items-center gap-sm py-sm', {
                      'pl-lg': cell.column.id === 'title',
                    })}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                );
              }

              let metricValue = 0;

              if (cell.column.id === 'streamsRTD') {
                metricValue = streaming?.aggregated?.totalStreams ?? 0;
              } else if (cell.column.id === 'streams') {
                metricValue =
                  streaming?.aggregated?.periodStreams?.current ?? 0;
              } else if (cell.column.id === 'organicStreams') {
                metricValue =
                  streaming?.aggregated?.organicStreams?.current ?? 0;
              } else if (cell.column.id === 'socialCreations') {
                metricValue =
                  engagement?.aggregated?.socialCreations?.current ?? 0;
              }

              const isTooltipEnabled =
                isCompareBenchmarkEnabled &&
                isPerformanceColumn &&
                !!streamingBenchmark && // Have to have a benchmark to compare against.
                !!streaming?.aggregated?.totalStreams; // Have to have a stream count to display the tooltip.

              return (
                <Tooltip
                  key={cell.id}
                  content={
                    <TrackPerformanceTooltip
                      metricType={
                        cell.column.id === 'streamsRTD'
                          ? 'streams'
                          : cell.column.id
                      }
                      metricTimeRange={
                        cell.column.id === 'streamsRTD' ? 'RTD' : 'TW'
                      }
                      metricValue={metricValue}
                      defaultBenchmark={streamingBenchmark ?? 0}
                      adjustedBenchmark={adjustedBenchmark}
                      adjustedBy={adjustedBy}
                      adjustmentReason={adjustmentReason}
                    />
                  }
                  delayDuration={1}
                  triggerAsChild
                  contentClassName="rounded-sm border-px p-md bg-level-03 border-emphasis-subtle"
                  side="bottom"
                  sideOffset={2}
                  align="start"
                  enabled={isTooltipEnabled}
                >
                  <TableCell
                    key={cell.id}
                    scope="row"
                    className={cn(
                      'border-y p-0 transition-all duration-300 ease-out border-transparent last:py-xs',
                      'group-hover/row:border-emphasis-strong',
                      'first:rounded-l-sm first:border-l',
                      'last:rounded-r-sm last:border-r last:pr-sm',
                      index % 2 === 0 && 'bg-default-inverse/default',
                      !isAssetPerformanceEnabled && 'first:pl-lg',
                    )}
                  >
                    {content}
                  </TableCell>
                </Tooltip>
              );
            })}
          </TableRow>
        );
      })}
    </TableBody>
  );
}

TrackTableBody.displayName = 'TrackTableBody';
